import {ImageOpNodeBrushStrokes} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/brush-strokes/node"
import {ToolMouseEvent} from "@common/helpers/canvas/canvas-base-toolbox-legacy/canvas-base-toolbox-item"
import {BrushSettings, BrushStroke} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/brush-strokes/definition"
import {Vector2, Vector2Like} from "@cm/math"
import {EventEmitter} from "@angular/core"
import paper from "paper"
import {AnyImageOpResult, ValueOrNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {TilingCanvasToolboxItemBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/tiling-canvas-toolbox-item-base"
import {TilingCanvasBaseToolboxItem} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/tiling-canvas-toolbox-item"
import {deepCopy} from "@cm/utils"
import {CanvasToolboxItem} from "@common/helpers/canvas/canvas-toolbox/canvas-toolbox-item"

export type Args = {
    sourceImage: ValueOrNode<AnyImageOpResult>
}

export class BrushTool extends TilingCanvasToolboxItemBase {
    readonly hoverPositionChanged = new EventEmitter<Vector2 | undefined>()
    readonly brushStrokeUpdated = new EventEmitter<void>()
    readonly brushStrokeCompleted = new EventEmitter<void>()

    constructor(parent: TilingCanvasBaseToolboxItem) {
        super(parent)
        this.cursor = "crosshair"

        this.brushLine = new paper.Path.Line(new paper.Point(0, 0), new paper.Point(0, 0))
        this.brushLine.strokeColor = new paper.Color("white")
        this.brushLine.visible = false

        this.callback.viewChange.subscribe(() => this.updateBrushCircle(true))
        this.selectedChange.subscribe((selected) => this.hoverPositionChanged.emit(selected ? this.lastHoverPosition : undefined))
        this.hoverPositionChanged.subscribe(() => this.updateBrushCircle())
    }

    override remove() {
        this.imageOpNodeBag.dispose()
        super.remove()
    }

    get hasEdits() {
        return this._brushStrokes.length > 0
    }

    get currentBrushStroke(): BrushStroke | undefined {
        return this._currentBrushStroke
    }

    get brushStrokes(): BrushStroke[] {
        return this._brushStrokes
    }

    copyFrom(other: BrushTool) {
        this._brushStrokes = deepCopy(other._brushStrokes)
    }

    removeAllStrokes() {
        this._brushStrokes.length = 0
        //this.onStrokePathChanged()
    }

    addStroke(stroke: BrushStroke) {
        this._brushStrokes.push(stroke)
        //this.onStrokePathChanged()
    }

    getImageOpNodes(args: Args) {
        this.imageOpNodeBag.beginFrame()
        const brushStrokes = [...this._brushStrokes]
        if (this._currentBrushStroke) {
            brushStrokes.push(this._currentBrushStroke)
        }
        const nodeBrushStrokes = this.imageOpNodeBag.getNode("strokes", ImageOpNodeBrushStrokes, {
            sourceImage: args.sourceImage,
            brushStrokes: brushStrokes,
            options: {
                tilingEnabled: true,
            },
        })
        const resultImage = this.imageOpNodeBag.getProperty("resultImage", nodeBrushStrokes, "resultImage")
        const boundingBox = this.imageOpNodeBag.getProperty("boundingBox", nodeBrushStrokes, "boundingBox")
        this.imageOpNodeBag.endFrame()
        return {
            resultImage,
            boundingBox,
        }
    }

    override hitTest(point: Vector2Like): CanvasToolboxItem | null {
        if (this.selected && this.callback.isPointInImage(point)) {
            this.lastHoverPosition = Vector2.fromVector2Like(point)
            return this
        }
        if (this.brushCircle) {
            this.brushCircle.visible = false
        }
        this.brushLine.visible = false
        this.lastHoverPosition = undefined
        return null
    }

    override onMouseLeave(_event: ToolMouseEvent): boolean {
        this.lastHoverPosition = undefined
        return false
    }

    set brushSettings(value: BrushSettings) {
        this._brushSettings = value
    }

    get brushSettings(): BrushSettings {
        return this._brushSettings
    }

    get isDrawing(): boolean {
        return !!this._currentBrushStroke
    }

    get brushStartPosition(): Vector2 | null {
        return this._brushStartPosition
    }

    set brushStartPosition(value: Vector2 | null) {
        this._brushStartPosition = value
    }

    get brushCurrentPosition(): Vector2 | null {
        return this._brushCurrentPosition
    }

    get brushCursorOffset(): Vector2 {
        return this._brushCursorOffset
    }

    override onMouseDown(event: ToolMouseEvent): boolean {
        super.onMouseDown(event)
        this.startBrushStroke(event.point)
        return false
    }

    override onMouseUp(event: ToolMouseEvent): boolean {
        super.onMouseUp(event)
        this.endBrushStroke(event.point)
        return false
    }

    override onMouseDrag(event: ToolMouseEvent): boolean {
        super.onMouseDrag(event)
        this.lastHoverPosition = Vector2.fromVector2Like(event.point)
        this.drawBrushStroke(event.lastPoint, event.point)
        return false
    }

    override onMouseMove(event: ToolMouseEvent): boolean {
        super.onMouseMove(event)
        this.lastHoverPosition = Vector2.fromVector2Like(event.point)
        return false
    }

    override onKeyDown(event: KeyboardEvent): boolean {
        super.onKeyDown(event)
        if (event.key === this.drawLineKey) {
            this.drawLine = true
        }
        return false
    }

    override onKeyUp(event: KeyboardEvent): boolean {
        super.onKeyUp(event)
        if (event.key === this.drawLineKey) {
            this.drawLine = false
        }
        return false
    }

    private set lastHoverPosition(value: Vector2 | undefined) {
        if ((!this._lastHoverPosition && !value) || (this._lastHoverPosition && value && this._lastHoverPosition.equals(value))) {
            return
        }
        this._lastHoverPosition = value
        this.hoverPositionChanged.emit(value)
    }

    get lastHoverPosition(): Vector2 | undefined {
        return this._lastHoverPosition
    }

    private getCursorPosition(toolPosition: Vector2Like): Vector2 {
        return new Vector2(Math.floor(this.brushCursorOffset.x + toolPosition.x), Math.floor(this.brushCursorOffset.y + toolPosition.y))
    }

    private updateBrushCircle(force = false) {
        const radius = this.brushSettings.width / 2
        if (force || !this.brushCircle || this.brushCircleRadius !== radius) {
            this.brushCircleRadius = radius
            if (this.brushCircle) {
                this.brushCircle.remove()
            }
            this.beginPaperCreation()
            this.brushCircle = new paper.Path.Circle(new paper.Point(0, 0), radius)
            this.brushCircle.strokeColor = new paper.Color("white")
            this.brushCircle.strokeWidth = 1 / this.callback.zoomLevel
            // this.brushCircle.blendMode = "difference"   // this has no effect for some reason (probably because the circle and the background image are on different canvases)
            this.brushCircle.visible = false
        }
        const cursorPosition = this.callback.canvasCursorPosition
        this.brushCircle.visible = this._lastHoverPosition !== undefined && this.selected
        if (this.brushCircle.visible) {
            this.brushCircle.position = new paper.Point(cursorPosition)
        }
        this.brushLine.visible = this.brushCircle.visible && this.drawLine
        if (this.brushLine.visible) {
            this.brushLine.strokeWidth = 1 / this.callback.zoomLevel
            this.brushLine.segments[0].point = new paper.Point(this.lastStrokePoint!.x, this.lastStrokePoint!.y)
            this.brushLine.segments[1].point = new paper.Point(cursorPosition.x, cursorPosition.y)
        }
    }

    private startBrushStroke(point: Vector2Like) {
        const position = this.getCursorPosition(point)
        //if (!this._brushStartPosition) {
        this._brushStartPosition = position
        this._brushCurrentPosition = this._brushStartPosition.clone()
        //}
        if (this.drawLine) {
            this._currentBrushStroke = {
                settings: {
                    ...this._brushSettings,
                },
                path: [this.getCursorPosition(this.lastStrokePoint), this._brushStartPosition.clone()],
            }
        } else {
            this._currentBrushStroke = {
                settings: {
                    ...this._brushSettings,
                },
                path: [],
            }
            this._currentBrushStroke.path.push(position)
        }
        this.lastStrokePoint = point
        this.onStrokePathChanged()
    }

    private drawBrushStroke(_lastPoint: Vector2Like, point: Vector2Like) {
        if (!this._currentBrushStroke) {
            return
        }
        const position = this.getCursorPosition(point)
        const minStrokeDistanceInScreenPixels = 3
        const minStrokeDistance = minStrokeDistanceInScreenPixels / this.callback.zoomLevel
        const delta = Vector2.fromVector2Like(point).sub(this.lastStrokePoint)
        if (delta.norm() >= minStrokeDistance) {
            this.lastStrokePoint = point
            // set up stroke geometry
            this._currentBrushStroke.path.push(position)
            this.onStrokePathChanged()
        }
        this._brushCurrentPosition?.setFromVector2Like(position)
    }

    private endBrushStroke(_point: Vector2Like) {
        if (!this._currentBrushStroke) {
            return
        }
        this.finalizeStroke()
        this._brushStartPosition = null
    }

    private onStrokePathChanged() {
        this.brushStrokeUpdated.emit()
    }

    private finalizeStroke() {
        if (!this._currentBrushStroke) {
            throw new Error("No current brush stroke")
        }
        this._brushStrokes.push(this._currentBrushStroke)
        this.brushStrokeCompleted.emit()
        this._currentBrushStroke = undefined
    }

    private imageOpNodeBag = new ImageOpNodeBag("BrushTool")

    private _brushStrokes: BrushStroke[] = []
    private _currentBrushStroke?: BrushStroke

    private _brushSettings: BrushSettings = {
        color: 1,
        width: 150,
        hardness: 1,
    }
    private lastStrokePoint: Vector2Like = {x: 0, y: 0}
    private _lastHoverPosition: Vector2 | undefined = undefined
    private _brushStartPosition: Vector2 | null = null
    private _brushCurrentPosition: Vector2 | null = null
    private _brushCursorOffset = new Vector2(0, 0)
    private brushCircle: paper.Path.Circle | null = null
    private brushLine: paper.Path.Line
    private brushCircleRadius = 0
    private drawLine = false
    private readonly drawLineKey = "Shift"
}
